import React, { useEffect, useState } from "react";
import { User, LogOut, Settings } from "lucide-react";
import PremiumHeader from "../components/ui/PremiumHeader";
import MembershipStatsCard from "../components/ui/MembershipStatsCard";
import HistoryEventCard from "../components/ui/HistoryEventCard";
import MatchHistoryDetailModal from "../components/modals/MatchHistoryDetailModal";
import { useAuth } from "../hooks";
import { useMypageContext } from "../context/MypageContext";
import { UI_TEXT } from "../constants/uiText";
import type {
  MypageParticipatedEvent,
  MypageParticipatedMatchResult,
} from "../types/swagger";
import { getJstYearLabel } from "../utils/eventSections";

interface ProfileViewProps {
  onLogout: () => void;
  onNotificationClick: () => void;
  onSettingsClick: () => void;
}

type FlatEvent = {
  event_name: string;
  event_date: string;
  singles_rank: number;
  singles_point: number;
  event_group_id?: string;
  event_group_name?: string;
  match_results: MypageParticipatedMatchResult[];
};

const ALL_EVENT_GROUPS = "__all__";

const flattenParticipatedEvents = (
  participated: MypageParticipatedEvent[],
): FlatEvent[] =>
  participated.flatMap(({ event_group, events }) =>
    events.map((e) => ({
      ...e,
      event_group_id: event_group?.id,
      event_group_name: event_group?.name,
      match_results: e.match_results ?? [],
    })),
  );

const groupEventsByYear = (events: FlatEvent[]) => {
  const sorted = [...events].sort(
    (left, right) =>
      new Date(right.event_date ?? "").getTime() -
      new Date(left.event_date ?? "").getTime(),
  );

  return sorted.reduce<Array<{ year: string; events: FlatEvent[] }>>(
    (groups, event) => {
      const year = getJstYearLabel(event.event_date ?? "");
      const currentGroup = groups[groups.length - 1];

      if (!currentGroup || currentGroup.year !== year) {
        groups.push({ year, events: [event] });
        return groups;
      }

      currentGroup.events.push(event);
      return groups;
    },
    [],
  );
};

const ProfileView: React.FC<ProfileViewProps> = ({
  onLogout,
  onNotificationClick,
  onSettingsClick,
}) => {
  const { mypage, isLoading, error, refresh } = useMypageContext();
  const { logout } = useAuth();
  const [selectedEvent, setSelectedEvent] = useState<FlatEvent | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedEventGroupId, setSelectedEventGroupId] =
    useState<string>(ALL_EVENT_GROUPS);
  const [hasInitializedEventGroup, setHasInitializedEventGroup] =
    useState(false);

  const participatedEventGroups = mypage?.participated_events ?? [];

  const eventGroupFilters = participatedEventGroups.map(({ event_group }) => ({
    id: event_group.id,
    name: event_group.name,
  }));

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (eventGroupFilters.length === 0) {
      setSelectedEventGroupId(ALL_EVENT_GROUPS);
      setHasInitializedEventGroup(false);
      return;
    }

    if (
      !hasInitializedEventGroup &&
      selectedEventGroupId === ALL_EVENT_GROUPS
    ) {
      const preferredId = mypage?.event_group?.id;
      if (
        preferredId &&
        eventGroupFilters.some((group) => group.id === preferredId)
      ) {
        setSelectedEventGroupId(preferredId);
      }
      setHasInitializedEventGroup(true);
      return;
    }

    if (!eventGroupFilters.some((group) => group.id === selectedEventGroupId)) {
      setSelectedEventGroupId(ALL_EVENT_GROUPS);
    }
  }, [
    eventGroupFilters,
    hasInitializedEventGroup,
    mypage?.event_group?.id,
    selectedEventGroupId,
  ]);

  const filteredParticipatedEvents =
    selectedEventGroupId === ALL_EVENT_GROUPS
      ? participatedEventGroups
      : participatedEventGroups.filter(
          ({ event_group }) => event_group?.id === selectedEventGroupId,
        );

  const eventHistoryByYear = groupEventsByYear(
    flattenParticipatedEvents(filteredParticipatedEvents),
  );

  const handleLogout = async () => {
    await logout();
    onLogout();
  };

  const handleRefresh = async () => {
    if (isRefreshing) {
      return;
    }

    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-300 overflow-hidden">
      <div className="overflow-y-auto scrollbar-hide flex-1">
        {" "}
        <PremiumHeader
          title={UI_TEXT.profile.title}
          icon={User}
          theme="emerald"
          onNotificationClick={onNotificationClick}
          onRefreshClick={handleRefresh}
          isRefreshing={isRefreshing}
        />
        <div className="px-6 py-6 pb-40 text-center">
          <MembershipStatsCard
            points={mypage?.yearly_point ?? "-"}
            rank={mypage?.yearly_rank ?? "-"}
            dartsliveName={mypage?.dartslive_card_name}
            eventGroupName={mypage?.event_group?.name}
            theme="emerald"
          />
          <button
            onClick={onSettingsClick}
            className="w-full bg-white border border-slate-200 text-slate-600 font-bold tracking-wider uppercase rounded-xl py-4 flex items-center justify-center gap-2 shadow-sm active:scale-95 mb-3 transition-all hover:bg-emerald-50 hover:text-emerald-600"
          >
            <Settings className="w-5 h-5" /> {UI_TEXT.profile.settings}
          </button>
          <button
            onClick={handleLogout}
            className="w-full bg-white border border-slate-200 text-slate-600 font-bold tracking-wider uppercase rounded-xl py-4 flex items-center justify-center gap-2 shadow-sm active:scale-95 mb-10 transition-all hover:bg-rose-50 hover:text-rose-600"
          >
            <LogOut className="w-5 h-5" /> {UI_TEXT.profile.logout}
          </button>
          <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 pl-1 text-left">
            {UI_TEXT.profile.matchHistory}
          </h2>
          {eventGroupFilters.length > 0 && (
            <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
              <button
                type="button"
                onClick={() => setSelectedEventGroupId(ALL_EVENT_GROUPS)}
                className={`shrink-0 rounded-full border px-3 py-1.5 text-[10px] font-bold tracking-wider transition-colors ${
                  selectedEventGroupId === ALL_EVENT_GROUPS
                    ? "border-emerald-500 bg-emerald-50 text-emerald-600"
                    : "border-slate-200 bg-white text-slate-500"
                }`}
              >
                {UI_TEXT.profile.matchHistoryAll}
              </button>
              {eventGroupFilters.map((group) => (
                <button
                  key={`event-group-filter-${group.id}`}
                  type="button"
                  onClick={() => setSelectedEventGroupId(group.id)}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-[10px] font-bold tracking-wider transition-colors ${
                    selectedEventGroupId === group.id
                      ? "border-emerald-500 bg-emerald-50 text-emerald-600"
                      : "border-slate-200 bg-white text-slate-500"
                  }`}
                >
                  {group.name ?? UI_TEXT.profile.unknownEventGroup}
                </button>
              ))}
            </div>
          )}
          {isLoading && (
            <p className="text-slate-400 text-xs font-bold text-center py-8">
              {UI_TEXT.common.loading}
            </p>
          )}
          {error && (
            <p className="text-rose-500 text-xs font-bold text-center py-8">
              {error}
            </p>
          )}
          {!isLoading &&
            !error &&
            eventHistoryByYear.map(({ year, events }) => (
              <section key={year} className="mb-8 text-left last:mb-0">
                <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 pl-1">
                  {`${year}`}
                </h3>
                {events.map((event, index) => (
                  <HistoryEventCard
                    key={`${year}-${event.event_name}-${event.event_date}-${index}`}
                    title={event.event_name ?? ""}
                    date={event.event_date ?? ""}
                    rank={event.singles_rank ?? 0}
                    points={event.singles_point ?? 0}
                    eventGroupName={event.event_group_name}
                    onClick={() => setSelectedEvent(event)}
                  />
                ))}
              </section>
            ))}
        </div>{" "}
      </div>{" "}
      <MatchHistoryDetailModal
        isOpen={selectedEvent !== null}
        onClose={() => setSelectedEvent(null)}
        eventName={selectedEvent?.event_name ?? ""}
        eventDate={selectedEvent?.event_date ?? ""}
        singlesRank={selectedEvent?.singles_rank ?? 0}
        singlesPoint={selectedEvent?.singles_point ?? 0}
        matchResults={selectedEvent?.match_results ?? []}
      />
    </div>
  );
};

export default ProfileView;
